import { useState } from 'react';
import { uploadDataset } from '../../utils/fine-tuning-functions';

export default function FileUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, message: null });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    // Clear any previous feedback when user selects a new file
    if (feedback.message) setFeedback({ type: null, message: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setFeedback({
        type: "error",
        message: "Please select a file to upload"
      });
      return;
    }
    
    if (!selectedFile.name.endsWith(".csv")) {
      setFeedback({
        type: "error",
        message: "Please upload a .csv file. Other file formats are not currently supported."
      });
      return;
    }
    
    setIsLoading(true);
    setFeedback({ type: null, message: null });
    
    try {
      await uploadDataset(selectedFile);
      
      setFeedback({
        type: "success",
        message: `Successfully uploaded dataset: ${selectedFile.name}`
      });
      setSelectedFile(null);
      // Reset the file input
      document.getElementById('file-upload').value = '';
    } catch (error) {
      console.error("Error uploading file:", error);
      setFeedback({
        type: "error",
        message: error.message || "Failed to upload dataset. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Upload Dataset File</h3>
        <p className="text-gray-600">
          Upload a CSV file containing your training data for fine-tuning.
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-1">
            Select File
          </label>
          <div className="mt-1">
            <div className="flex items-center">
              <label
                htmlFor="file-upload"
                className="relative flex cursor-pointer items-center rounded-md bg-white py-2 px-3 shadow-sm border border-gray-300 text-sm font-medium text-gray-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-ktx-purple focus-within:ring-offset-2 hover:bg-gray-50 flex-1"
              >
                <div className="flex items-center w-full">
                  <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="truncate">
                    {selectedFile ? selectedFile.name : "Choose a file..."}
                  </span>
                </div>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  onChange={handleFileChange}
                  accept=".csv"
                  disabled={isLoading}
                />
              </label>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">Only CSV files are currently supported</p>
        </div>
        
        {feedback.message && (
          <div className={`p-3 rounded-md ${
            feedback.type === 'error' 
              ? 'bg-red-50 text-red-700 border border-red-200' 
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {feedback.type === 'error' ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm">{feedback.message}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`bg-ktx-purple hover:bg-ktx-purple-dark text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:ring-offset-2 transition-colors
            ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </div>
            ) : (
              'Upload Dataset'
            )}
          </button>
        </div>
      </form>
      
      <div className="mt-8 p-4 bg-blue-50 rounded-md border border-blue-100">
        <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Requirements</h4>
        <ul className="space-y-1 text-sm text-blue-700">
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Headers should be clearly labeled</span>
          </li>
          <li className="flex items-start">
            <svg className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>For best results, include 100+ training examples</span>
          </li>
        </ul>
      </div>
    </div>
  );
}